import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import { campaignService } from '../../services/campaignService';
import { Campaign } from '../../types/campaign';
import {
  ImportPhoneValidationResult,
  ImportPhoneValidationStatus,
} from '../../types/lead';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import { Table, Column } from '../../components/UI/Table';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';

interface ImportError {
  row: number;
  field?: string;
  message: string;
  data: Record<string, any>;
}

interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  errors: ImportError[];
}

interface LeadPreview {
  row: number;
  full_name: string;
  phone_e164: string;
  phone_e164_2?: string;
  phone_e164_3?: string;
  email?: string;
  company_name?: string;
  custom?: Record<string, any>;
  status?: ImportPhoneValidationStatus;
  duplicateOfRow?: number;
  existingLeadName?: string;
}

const VALIDATION_ROW_LIMIT = 5000;

const ImportLeadsPage: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [file, setFile] = useState<File | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    total: number;
    imported: number;
    skipped: number;
    errors: number;
    status: string;
  } | null>(null);
  const [preview, setPreview] = useState<LeadPreview[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [validation, setValidation] = useState<ImportPhoneValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (selectedFile: File) => {
    if (!selectedFile) return;
    
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Please select a CSV or Excel file.');
      return;
    }
    
    setFile(selectedFile);
    setImportResult(null);
    parseFilePreview(selectedFile);
  };

  const parseFilePreview = async (file: File) => {
    // Helper: very small CSV parser that handles quoted values and commas
    const parseCsv = (text: string): string[][] => {
      const lines = text.split(/\r\n|\n/).filter(Boolean);
      return lines.map((line) => {
        const row: string[] = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
              // escaped quote
              cur += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (ch === ',' && !inQuotes) {
            row.push(cur.trim());
            cur = '';
          } else {
            cur += ch;
          }
        }
        row.push(cur.trim());
        return row;
      });
    };

    // Map a header row + data rows into LeadPreview objects.
    const rowsToLeads = (rows: string[][]): LeadPreview[] => {
      if (!rows || rows.length < 2) return [];
      const header = rows[0].map((h) => h.toLowerCase().trim());
      const data = rows.slice(1).filter((r) => r.some((c) => (c || '').toString().trim().length > 0));

      return data.map((row, index) => {
        const obj: Record<string, string> = {};
        header.forEach((h, i) => {
          obj[h] = (row[i] ?? '').toString().trim();
        });

        // Primary fields (support slight variations in header names)
        const full_name = obj['full_name'] || obj['full name'] || `${obj['first_name'] || ''} ${obj['last_name'] || ''}`.trim();
        const phone_e164 = obj['phone_e164'] || obj['phone'] || obj['phone e164'] || '';
        const email = obj['email'] || '';
        const company_name = obj['company_name'] || obj['company'] || obj['company name'] || '';

        const phone_e164_2 = obj['phone_e164_2'] || obj['phone_2'] || obj['phone e164 2'] || '';
        const phone_e164_3 = obj['phone_e164_3'] || obj['phone_3'] || obj['phone e164 3'] || '';

        // Custom fields are the remaining columns
        const custom: Record<string, any> = {};
        const reservedKeys = ['full_name', 'full name', 'first_name', 'last_name', 'phone_e164', 'phone', 'phone e164', 'phone_e164_2', 'phone_2', 'phone e164 2', 'phone_e164_3', 'phone_3', 'phone e164 3', 'email', 'company', 'company_name', 'company name', 'external_ref'];
        Object.keys(obj).forEach((k) => {
          const isPhoneColumn = k.startsWith('phone_e164');
          if (!reservedKeys.includes(k) && !isPhoneColumn) {
            if (obj[k] !== undefined && obj[k] !== '') custom[k] = obj[k];
          }
        });

        return {
          row: index + 2,
          full_name: full_name || '',
          phone_e164: phone_e164 || '',
          phone_e164_2: phone_e164_2 || undefined,
          phone_e164_3: phone_e164_3 || undefined,
          email: email || undefined,
          company_name: company_name || undefined,
          custom: Object.keys(custom).length ? custom : undefined
        } as LeadPreview;
      });
    };

    const runPhoneValidation = async (leads: LeadPreview[]) => {
      if (!campaignId || leads.length === 0) {
        setValidation(null);
        return leads;
      }

      const sampleLeads =
        leads.length > VALIDATION_ROW_LIMIT ? leads.slice(0, VALIDATION_ROW_LIMIT) : leads;
      if (leads.length > VALIDATION_ROW_LIMIT) {
        toast(
          `Validating first ${VALIDATION_ROW_LIMIT.toLocaleString()} of ${leads.length.toLocaleString()} rows. Full duplicate check runs during import.`,
          { icon: 'ℹ️' }
        );
      }

      try {
        setValidating(true);
        const result = await leadService.validateImportPhones(
          campaignId,
          sampleLeads.map((lead) => ({ row: lead.row, phone: lead.phone_e164 }))
        );
        setValidation(result);

        const statusByRow = new Map(result.rows.map((r) => [r.row, r]));
        return leads.map((lead) => {
          const match = statusByRow.get(lead.row);
          if (!match) return lead;
          return {
            ...lead,
            phone_e164: match.phone || lead.phone_e164,
            status: match.status,
            duplicateOfRow: match.duplicateOfRow,
            existingLeadName: match.existingLeadName,
          };
        });
      } catch (error) {
        console.error('Failed to validate import phones:', error);
        toast.error('Could not check for duplicate phone numbers. You can still import.');
        setValidation(null);
        return leads;
      } finally {
        setValidating(false);
      }
    };

    try {
      setUploading(true);

      const name = file.name.toLowerCase();

      // CSV parsing (native)
      if (name.endsWith('.csv') || file.type === 'text/csv') {
        const text = await file.text();
        const rows = parseCsv(text);
        const leads = rowsToLeads(rows);
        const validatedLeads = await runPhoneValidation(leads);
        setPreview(validatedLeads.slice(0, 1000));
        setShowPreview(true);
        return;
      }

      // For Excel files try to dynamically import the `xlsx` (SheetJS) package.
      if (name.endsWith('.xls') || name.endsWith('.xlsx') || file.type === 'application/vnd.ms-excel' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        try {
          const XLSX = await import('xlsx');
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const raw: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          // If the first row is the header, pass through rowsToLeads which expects header + rows
          const leads = rowsToLeads(raw as string[][]);
          const validatedLeads = await runPhoneValidation(leads);
          setPreview(validatedLeads.slice(0, 1000));
          setShowPreview(true);
          return;
        } catch (err) {
          console.error('Failed to parse Excel file (xlsx import/error):', err);
          toast.error('To parse Excel files in the browser please add the "xlsx" package to the frontend or upload a CSV.');
          return;
        }
      }

      toast.error('Unsupported file type. Please upload a CSV or Excel file.');
    } catch (error) {
      console.error('Failed to parse file:', error);
      toast.error('Failed to parse file. Please check the format and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !campaignId) return;
    
    try {
      setUploading(true);
      setImportProgress(null);
      
      // Create field mapping - for now using default mapping
      const mapping = {
        fullName: 'full_name',
        phoneE164: 'phone_e164',
        email: 'email',
        companyName: 'company',
        externalRef: 'external_ref'
      };
      
      // Transform mapping to backend format (camelCase keys to snake_case)
      const backendMapping: Record<string, string> = {
        full_name: mapping.fullName,
        phone_e164: mapping.phoneE164,
        email: mapping.email,
        company_name: mapping.companyName,
        external_ref: mapping.externalRef,
      };
      // Detect extra phone columns in the uploaded file header
      const text = await file.text();
      const rows = text.split(/\r\n|\n/).filter(Boolean);
      const header = rows.length > 0 ? rows[0].split(',').map((h) => h.trim().toLowerCase().replace(/^"|"$/g, '')) : [];
      for (const key of header) {
        if (key.startsWith('phone_e164') && key !== 'phone_e164') {
          backendMapping[key] = key;
        }
      }

      // Backward-compat explicit checks for older preview paths
      if (preview.some((l) => l.phone_e164_2)) {
        backendMapping['phone_e164_2'] = 'phone_e164_2';
      }
      if (preview.some((l) => l.phone_e164_3)) {
        backendMapping['phone_e164_3'] = 'phone_e164_3';
      }
      // Auto-map custom columns so CSV imports can populate lead.custom
      const customKeys = new Set<string>();
      for (const lead of preview) {
        for (const key of Object.keys(lead.custom || {})) {
          customKeys.add(key);
        }
      }
      for (const key of customKeys) {
        if (!key.startsWith('phone_e164')) {
          backendMapping[key] = key;
        }
      }
      
      const result = await leadService.importLeads(
        campaignId,
        {
          file,
          mapping: backendMapping as any,
          skipDuplicates: true,
          updateExisting: false,
        },
        (status) => {
          setImportProgress({
            total: status.total || 0,
            imported: status.imported || 0,
            skipped: status.skipped || 0,
            errors: Array.isArray(status.errors) ? status.errors.length : 0,
            status: status.status || 'processing',
          });
        }
      );
      
      // Convert backend result to frontend format
      const importResult: ImportResult = {
        success: result.imported > 0 || (Array.isArray(result.errors) && result.errors.length === 0),
        imported: result.imported,
        skipped: result.skipped || 0,
        errors: (result.errors || []).map((err: any) => ({
          row: err.row,
          field: err.field,
          message: err.message || err.error || 'Unknown error',
          data: err.data || {}
        }))
      };
      
      setImportResult(importResult);
      setShowPreview(false);
      setImportProgress(null);
      if (importResult.imported > 0) {
        toast.success(`Imported ${importResult.imported.toLocaleString()} leads`);
      }
    } catch (error: any) {
      console.error('Failed to import leads:', error);
      
      // Extract specific error message from backend response
      let errorMessage = 'Failed to import leads. Please try again.';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        // Backend sends: { error: { message: "...", code: "...", statusCode: ... } }
        if (apiError.response?.data?.error?.message) {
          errorMessage = apiError.response.data.error.message;
        } else if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setImportProgress(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const downloadTemplate = () => {
    // Create a sample CSV template with optional extra phone columns
    const csvContent = [
      'full_name,phone_e164,phone_e164_2,phone_e164_3,email,company,external_ref',
      'John Doe,+15551234567,+15559999999,,john@example.com,Acme Corp,LEAD001',
      'Jane Smith,+15551234568,,,jane@example.com,Tech Inc,LEAD002'
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const fetchCampaign = async () => {
    if (!campaignId) return;
    
    try {
      const campaignData = await campaignService.getCampaign(campaignId);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    }
  };

  React.useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  const getStatusLabel = (lead: LeadPreview): string => {
    switch (lead.status) {
      case 'new':
        return 'New';
      case 'duplicate_in_file':
        return lead.duplicateOfRow
          ? `Duplicate (row ${lead.duplicateOfRow})`
          : 'Duplicate in file';
      case 'existing':
        return lead.existingLeadName
          ? `Already exists (${lead.existingLeadName})`
          : 'Already exists';
      case 'invalid':
        return 'Invalid phone';
      default:
        return '—';
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview([]);
    setShowPreview(false);
    setImportResult(null);
    setValidation(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign?.name || 'Campaign', href: `/campaigns/${campaignId}` },
          { label: 'Leads', href: `/campaigns/${campaignId}/leads` },
          { label: 'Import Leads' }
        ]}
      />
      
      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <h1 className="text-2xl font-bold text-foreground">Import Leads</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload a CSV or Excel file to import leads into your campaign.
          </p>
        </CardContent>
      </Card>

      {/* Template Download */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <DocumentTextIcon className="h-5 w-5 text-primary/60" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-primary">
              Need a template?
            </h3>
            <p className="mt-1 text-sm text-primary/80">
              Download our CSV template to ensure your file has the correct format.
            </p>
            <div className="mt-3">
              <Button
                onClick={downloadTemplate}
                variant="outlined"
                size="sm"
                className="text-primary bg-primary/10 hover:bg-primary/20 border-primary/30"
              >
                <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Download Template
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload */}
      {!showPreview && !importResult && (
        <Card className="overflow-hidden">
          <CardContent className="px-4 py-5 sm:p-6">
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                dragOver
                  ? 'border-primary/60 bg-primary/5'
                  : 'border-border hover:border-border/80'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CloudArrowUpIcon className="h-8 w-8 text-primary" />
              </div>
              <div className="mt-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-semibold text-foreground">
                    {file ? file.name : 'Drop your file here, or click to browse'}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    CSV, XLS, XLSX up to 10MB
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  accept=".csv,.xls,.xlsx"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (selectedFile) {
                      handleFileSelect(selectedFile);
                    }
                  }}
                />
              </div>
              
              {file && (
                <div className="mt-4 flex items-center justify-center space-x-4">
                  <Button
                    onClick={() => parseFilePreview(file)}
                    disabled={uploading}
                    variant="primary"
                    loading={uploading}
                  >
                    {uploading ? 'Processing...' : 'Preview Import'}
                  </Button>
                  <Button
                    onClick={resetImport}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {showPreview && (
        <Card className="overflow-hidden">
          <CardContent className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  Preview Import ({preview.length} leads)
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Review the leads below before importing. Duplicate and existing phone numbers are checked automatically.
                </p>
              </div>
              <Button
                onClick={resetImport}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            {validating && (
              <div className="mb-4 flex items-center text-sm text-muted-foreground">
                <LoadingSpinner size="sm" />
                <span className="ml-2">Checking for duplicate phone numbers...</span>
              </div>
            )}

            {validation && !validating && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 mb-6">
                <div className="bg-success/10 p-4 rounded-lg border border-success/20">
                  <div className="text-2xl font-bold text-success">{validation.summary.newLeads}</div>
                  <div className="text-sm text-success/80">New leads</div>
                </div>
                <div className="bg-warning/10 p-4 rounded-lg border border-warning/20">
                  <div className="text-2xl font-bold text-warning">{validation.summary.duplicateInFile}</div>
                  <div className="text-sm text-warning/80">Duplicates in file</div>
                </div>
                <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                  <div className="text-2xl font-bold text-primary">{validation.summary.existingInCampaign}</div>
                  <div className="text-sm text-primary/80">Already in campaign</div>
                </div>
                <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/20">
                  <div className="text-2xl font-bold text-destructive">{validation.summary.invalid}</div>
                  <div className="text-sm text-destructive/80">Invalid phones</div>
                </div>
              </div>
            )}
            
            <Table
              data={preview.slice(0, 10)}
              columns={[
                {
                  key: 'status',
                  header: 'Status',
                  accessor: 'status',
                  render: (lead) => {
                    const label = getStatusLabel(lead);
                    const colorClass =
                      lead.status === 'new'
                        ? 'text-success'
                        : lead.status === 'existing'
                          ? 'text-primary'
                          : lead.status === 'duplicate_in_file'
                            ? 'text-warning'
                            : lead.status === 'invalid'
                              ? 'text-destructive'
                              : 'text-muted-foreground';
                    return <span className={`text-sm font-medium ${colorClass}`}>{label}</span>;
                  }
                },
                {
                  key: 'full_name',
                  header: 'Name',
                  accessor: 'full_name',
                  render: (lead) => (
                    <span className="font-medium text-foreground">{lead.full_name}</span>
                  )
                },
                {
                  key: 'phone_e164',
                  header: 'Phone',
                  accessor: 'phone_e164'
                },
                {
                  key: 'email',
                  header: 'Email',
                  accessor: 'email',
                  render: (lead) => lead.email || '-'
                },
                {
                  key: 'company_name',
                  header: 'Company',
                  accessor: 'company_name',
                  render: (lead) => lead.company_name || '-'
                },
                {
                  key: 'custom',
                  header: 'Custom Fields',
                  accessor: 'custom',
                  render: (lead) => {
                    if (lead.custom && Object.keys(lead.custom).length > 0) {
                      return Object.entries(lead.custom)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                    }
                    return '-';
                  }
                }
              ] as Column<LeadPreview>[]}
              size="sm"
            />
            
            {preview.length > 10 && (
              <p className="mt-4 text-sm text-muted-foreground text-center">
                Showing first 10 of {preview.length} leads
              </p>
            )}
            
            <div className="mt-6 flex items-center justify-end space-x-3">
              {importProgress && (
                <div className="mr-auto text-sm text-muted-foreground">
                  Processing {importProgress.imported.toLocaleString()} of{' '}
                  {Math.max(importProgress.total, importProgress.imported).toLocaleString()} leads
                  {importProgress.skipped > 0
                    ? ` (${importProgress.skipped.toLocaleString()} skipped)`
                    : ''}
                  …
                </div>
              )}
              <Button
                onClick={resetImport}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={uploading || validating || (validation?.summary.newLeads === 0)}
                variant="primary"
                loading={uploading}
                className="bg-success hover:bg-success/90"
              >
                {uploading ? 'Importing...' : (
                  <>
                    <CheckCircleIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    Import {validation ? validation.summary.newLeads : preview.length} Leads
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Result */}
      {importResult && (
        <Card>
          <CardContent className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              {importResult.success ? (
                <CheckCircleIcon className="h-8 w-8 text-success mr-3" />
              ) : (
                <ExclamationTriangleIcon className="h-8 w-8 text-destructive mr-3" />
              )}
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  {importResult.success ? 'Import Completed' : 'Import Failed'}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {importResult.success
                    ? `Successfully imported ${importResult.imported} leads.`
                    : 'There were errors during the import process.'
                  }
                </p>
              </div>
            </div>
            
            {importResult.success && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
                <div className="bg-success/10 p-4 rounded-lg border border-success/20">
                  <div className="text-2xl font-bold text-success">
                    {importResult.imported}
                  </div>
                  <div className="text-sm text-success/80">Imported</div>
                </div>
                <div className="bg-warning/10 p-4 rounded-lg border border-warning/20">
                  <div className="text-2xl font-bold text-warning">
                    {importResult.skipped}
                  </div>
                  <div className="text-sm text-warning/80">Skipped</div>
                </div>
                <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/20">
                  <div className="text-2xl font-bold text-destructive">
                    {importResult.errors.length}
                  </div>
                  <div className="text-sm text-destructive/80">Errors</div>
                </div>
              </div>
            )}
            
            {importResult.errors.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-foreground mb-3">Errors:</h4>
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                  <div className="space-y-2">
                    {importResult.errors.map((error, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-destructive">
                          Row {error.row}, {error.field}:
                        </span>
                        <span className="text-destructive/80 ml-1">
                          {error.message}
                          {error.data && Object.keys(error.data).length > 0 && ` (${JSON.stringify(error.data)})`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-end space-x-3">
              <Button
                onClick={resetImport}
                variant="outlined"
              >
                Import More
              </Button>
              <Button
                onClick={() => navigate(`/campaigns/${campaignId}/leads`)}
                variant="primary"
              >
                View Leads
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportLeadsPage;